/**
 * /sources — "Imported Signal history". Lists Signal backup imports and, for
 * each, every collection it created.
 *
 * Identifying a Signal batch needs no schema change: `selection` is written only
 * by the Signal select step and never cleared, so `selection !== null` is an
 * exact discriminator, and `selection.chats` carries the full collection set —
 * which `batch.collectionId` does not (it records only the first of many).
 *
 * Deliberately no ingestion toggle: an import is a one-off backfill with no live
 * stream to pause. Visibility is editable, since that is the only way to share an
 * imported DM (they land private).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Lock, User, Users } from 'lucide-react';
import { toast } from 'sonner';
import { IconTile } from '@/components/patterns/icon-tile';
import { StatusPill, type StatusPillTone } from '@/components/patterns/status-pill';
import { connectorApi, type ImportBatch, importsApi, type SignalImportCollection } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

const STATUS: Record<ImportBatch['status'], { label: string; tone: StatusPillTone }> = {
  parsing: { label: 'Importing', tone: 'active' },
  indexing: { label: 'Indexing', tone: 'active' },
  done: { label: 'Imported', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
};

function CollectionRow({ chat }: { chat: SignalImportCollection }) {
  const qc = useQueryClient();
  const visMut = useMutation({
    mutationFn: (v: 'workspace' | 'private') =>
      connectorApi.setCollectionVisibility(chat.collectionId, v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.imports }),
    onError: () => toast.error('Failed to update visibility'),
  });

  return (
    <div className="flex items-center gap-3 border-border-subtle border-b px-4 py-3 last:border-b-0">
      {chat.kind === 'group' ? (
        <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : (
        <User className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <span className="min-w-0 flex-1 truncate text-[14px]">{chat.title}</span>

      <div className="inline-flex overflow-hidden rounded-md border border-border text-[11.5px]">
        {(['workspace', 'private'] as const).map((v) => (
          <button
            key={v}
            type="button"
            disabled={visMut.isPending}
            onClick={() => chat.visibility !== v && visMut.mutate(v)}
            className={cn(
              'px-2 py-1 transition-colors',
              chat.visibility === v
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {v === 'workspace' ? 'Shared' : 'Private'}
          </button>
        ))}
      </div>
      {chat.visibility === 'private' && <Lock className="h-3 w-3 text-faint" />}
    </div>
  );
}

function ImportCard({ batch }: { batch: ImportBatch }) {
  const status = STATUS[batch.status];
  const chats = batch.selection?.chats ?? [];
  const counts = batch.counts;

  return (
    <div className="rounded-xl border border-border p-6">
      <div className="flex items-center gap-4">
        <IconTile tone="neutral">
          <Download className="h-5 w-5" />
        </IconTile>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-[15px]">{batch.filename}</p>
          <p className="truncate font-mono text-[13px] text-faint">
            {new Date(batch.createdAt).toLocaleDateString()} · {chats.length} chat
            {chats.length === 1 ? '' : 's'}
            {counts ? ` · ${counts.imported.toLocaleString()} messages` : ''}
          </p>
        </div>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
      </div>

      {batch.status === 'failed' && batch.error && (
        <p className="mt-3 text-[13px] text-destructive">{batch.error}</p>
      )}

      {chats.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-lg border border-border-subtle">
          {chats.map((chat) => (
            <CollectionRow key={chat.collectionId} chat={chat} />
          ))}
        </div>
      )}
    </div>
  );
}

export function SignalImports() {
  const { data } = useQuery({
    queryKey: qk.imports,
    queryFn: () => importsApi.list(),
  });

  // `selection` is written only by the Signal path, so this is exact. The page
  // already polls this key while any batch is in flight — no interval here.
  const signalBatches = (data?.batches ?? []).filter((b) => b.selection != null);
  if (signalBatches.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="font-semibold text-base">Imported Signal history</h2>
      <p className="mt-1 text-[13px] text-muted-foreground">
        Conversations backfilled from a Signal backup. Direct messages import as private to you.
      </p>
      <div className="mt-4 flex flex-col gap-4">
        {signalBatches.map((batch) => (
          <ImportCard key={batch.id} batch={batch} />
        ))}
      </div>
    </div>
  );
}
